import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { validarCNPJ } from "../lib/utils";
import { 
  CalendarDays, 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  User, 
  AlertCircle, 
  Building2, 
  FileText,
  MapPin,
  Loader2,
  CheckCircle2,
  Clock,
  Search,
  Phone
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription } from "../components/ui/alert";

export const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Dados pessoais
  const [nomeCompleto, setNomeCompleto] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  
  // Dados da farmácia
  const [cnpj, setCnpj] = useState("");
  const [razaoSocial, setRazaoSocial] = useState("");
  const [nomeFantasia, setNomeFantasia] = useState("");
  
  // Endereço
  const [cep, setCep] = useState("");
  const [logradouro, setLogradouro] = useState("");
  const [numero, setNumero] = useState("");
  const [complemento, setComplemento] = useState("");
  const [bairro, setBairro] = useState("");
  const [cidade, setCidade] = useState("");
  const [estado, setEstado] = useState("");
  const [buscandoCep, setBuscandoCep] = useState(false);
  
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cadastroPendente, setCadastroPendente] = useState(false);
  const [farmaciaRegistrada, setFarmaciaRegistrada] = useState("");

  // Pré-preencher campos com parâmetros da URL (vindo da LP)
  useEffect(() => {
    const nome = searchParams.get('nome');
    const emailParam = searchParams.get('email');
    const whatsappParam = searchParams.get('whatsapp');
    const farmacia = searchParams.get('farmacia');
    
    if (nome) setNomeCompleto(nome);
    if (emailParam) setEmail(emailParam);
    if (whatsappParam) setWhatsapp(whatsappParam);
    if (farmacia) setNomeFantasia(farmacia);
  }, [searchParams]);

  // Busca de endereço por CEP (ViaCEP)
  const handleCepSearch = async () => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) {
      setError("CEP deve ter 8 dígitos");
      return;
    }
    
    setBuscandoCep(true);
    setError("");
    
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        setError("CEP não encontrado");
        return;
      }
      
      setLogradouro(data.logradouro || "");
      setBairro(data.bairro || "");
      setCidade(data.localidade || "");
      setEstado(data.uf || "");
    } catch (err) {
      setError("Erro ao buscar CEP");
    } finally {
      setBuscandoCep(false);
    }
  };

  // Formatação de WhatsApp: +55 (XX) XXXXX-XXXX
  const formatWhatsapp = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 13) {
      let formatted = '';
      if (numbers.length > 0) {
        formatted = '+' + numbers.slice(0, 2);
      }
      if (numbers.length > 2) {
        formatted += ' (' + numbers.slice(2, 4);
      }
      if (numbers.length > 4) {
        formatted += ') ' + numbers.slice(4, 9);
      }
      if (numbers.length > 9) {
        formatted += '-' + numbers.slice(9, 13);
      }
      return formatted;
    }
    return value;
  };

  // Validação de WhatsApp (deve ter pelo menos 13 dígitos: 55 + DDD + 9 dígitos)
  const validarWhatsapp = (whatsapp) => {
    const numbers = whatsapp.replace(/\D/g, '');
    return numbers.length >= 12 && numbers.length <= 13;
  };

  const handleWhatsappChange = (e) => {
    setWhatsapp(formatWhatsapp(e.target.value));
  };

  // Validação de CNPJ
  const validarCNPJ = (cnpj) => {
    cnpj = cnpj.replace(/\D/g, '');
    if (cnpj.length !== 14) return false;
    if (/^(\d)\1+$/.test(cnpj)) return false;
    
    let tamanho = cnpj.length - 2;
    let números = cnpj.substring(0, tamanho);
    let digitos = cnpj.substring(tamanho);
    let soma = 0;
    let pos = tamanho - 7;
    
    for (let i = tamanho; i >= 1; i--) {
      soma += números.charAt(tamanho - i) * pos--;
      if (pos < 2) pos = 9;
    }
    
    let resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
    if (resultado !== parseInt(digitos.charAt(0))) return false;
    
    tamanho = tamanho + 1;
    números = cnpj.substring(0, tamanho);
    soma = 0;
    pos = tamanho - 7;
    
    for (let i = tamanho; i >= 1; i--) {
      soma += números.charAt(tamanho - i) * pos--;
      if (pos < 2) pos = 9;
    }
    
    resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
    if (resultado !== parseInt(digitos.charAt(1))) return false;
    
    return true;
  };

  // Formatacao de CNPJ
  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 14) {
      return numbers
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    return value;
  };

  const handleCNPJChange = (e) => {
    setCnpj(formatCNPJ(e.target.value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Validacoes
    if (!nomeCompleto.trim()) {
      setError("Informe seu nome completo");
      setLoading(false);
      return;
    }

    if (!validarWhatsapp(whatsapp)) {
      setError("WhatsApp inválido. Use o formato: +55 (XX) XXXXX-XXXX");
      setLoading(false);
      return;
    }

    if (!email.trim()) {
      setError("Informe seu email");
      setLoading(false);
      return;
    }

    if (senha.length < 6) {
      setError("A senha deve ter pelo menos 6 caracteres");
      setLoading(false);
      return;
    }

    if (!validarCNPJ(cnpj)) {
      setError("CNPJ inválido");
      setLoading(false);
      return;
    }

    if (!razaoSocial.trim()) {
      setError("Informe a razao social");
      setLoading(false);
      return;
    }

    if (!nomeFantasia.trim()) {
      setError("Informe o nome fantasia");
      setLoading(false);
      return;
    }

    if (!cep.trim() || !logradouro.trim() || !cidade.trim()) {
      setError("Informe o CEP e endereço completo");
      setLoading(false);
      return;
    }

    // Montar endereço completo
    const enderecoCompleto = `${logradouro}${numero ? ', ' + numero : ''}${complemento ? ' - ' + complemento : ''}, ${bairro}, ${cidade} - ${estado}, CEP: ${cep.replace(/\D/g, '').replace(/(\d{5})(\d{3})/, '$1-$2')}`;

    try {
      const response = await register({
        nome_completo: nomeCompleto,
        whatsapp: whatsapp.replace(/\D/g, ''),
        email,
        senha,
        cnpj: cnpj.replace(/\D/g, ''),
        razao_social: razaoSocial,
        nome_fantasia: nomeFantasia,
        endereco: enderecoCompleto
      });
      
      // Disparar evento sign_up no GTM
      if (window.dataLayer) {
        window.dataLayer.push({
          'event': 'sign_up',
          'method': 'registration_form'
        });
      }
      
      // Se retornar status pendente, mostrar mensagem
      if (response && response.status === "pendente") {
        setCadastroPendente(true);
        setFarmaciaRegistrada(response.farmacia_nome || nomeFantasia);
      } else {
        // Comportamento anterior - redireciona se login automatico
        navigate("/dashboard");
      }
    } catch (err) {
      setError(err.message || "Erro ao criar conta. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  // Tela de cadastro pendente
  if (cadastroPendente) {
    return (
      <div className="min-h-screen flex">
        <div className="flex-1 flex items-center justify-center p-8 bg-white">
          <div className="w-full max-w-md text-center">
            <div className="w-20 h-20 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-6">
              <Clock className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
              Cadastro Enviado!
            </h1>
            <p className="text-slate-600 mb-6">
              O cadastro de <strong>{farmaciaRegistrada}</strong> foi recebido com sucesso.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-semibold text-amber-800 mb-1">Aguardando Aprovacao</h3>
                  <p className="text-sm text-amber-700">
                    Seu cadastro será analisado pela equipe administrativa. 
                    Você receberá uma notificacao quando sua conta for aprovada.
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <p className="text-sm text-slate-500">
                Use as credenciais cadastradas para fazer login após a aprovacao.
              </p>
              <Button 
                onClick={() => navigate("/login")} 
                className="w-full bg-teal-600 hover:bg-teal-700"
              >
                Ir para Login
              </Button>
            </div>
          </div>
        </div>
        {/* Banner Section */}
        <div className="hidden lg:flex lg:flex-1 bg-gradient-to-br from-teal-600 to-teal-800 text-white p-12 flex-col justify-center">
          <div className="max-w-lg">
            <CheckCircle2 className="w-16 h-16 mb-6 text-teal-200" />
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>
              Quase la!
            </h2>
            <p className="text-lg text-teal-100 mb-8">
              Estamos verificando suas informações. Em breve você terá acesso completo ao sistema.
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-teal-500/30 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <span>Cadastro recebido</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-teal-500/30 rounded-full flex items-center justify-center">
                  <Clock className="w-5 h-5" />
                </div>
                <span>Análise em andamento</span>
              </div>
              <div className="flex items-center gap-3 opacity-50">
                <div className="w-8 h-8 bg-teal-500/30 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <span>Aprovacao e liberacao do acesso</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Form Section */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white overflow-y-auto">
        <div className="w-full max-w-md py-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center">
              <CalendarDays className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                Conforme a Resolução
              </h1>
              <p className="text-sm text-slate-500">Crie sua conta</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Seção: Dados Pessoais */}
            <div className="border-b border-slate-200 pb-2 mb-4">
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                Dados Pessoais
              </h2>
            </div>

            <div className="space-y-2">
              <Label htmlFor="nomeCompleto">Nome Completo *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="nomeCompleto"
                  type="text"
                  placeholder="Seu nome completo"
                  value={nomeCompleto}
                  onChange={(e) => setNomeCompleto(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="register-nome"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Admin *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="register-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="whatsapp">WhatsApp *</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="whatsapp"
                  type="text"
                  placeholder="+55 (XX) XXXXX-XXXX"
                  value={whatsapp}
                  onChange={handleWhatsappChange}
                  className="pl-10"
                  maxLength={19}
                  required
                  data-testid="register-whatsapp"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="senha">Senha *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="senha"
                  type={showPassword ? "text" : "password"}
                  placeholder="Mínimo 6 caracteres"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  className="pl-10 pr-10"
                  required
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Seção: Dados da Farmácia */}
            <div className="border-b border-slate-200 pb-2 mb-4 mt-6">
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                Dados da Farmácia
              </h2>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cnpj">CNPJ *</Label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="cnpj"
                  type="text"
                  placeholder="00.000.000/0000-00"
                  value={cnpj}
                  onChange={handleCNPJChange}
                  className="pl-10"
                  maxLength={18}
                  required
                  data-testid="register-cnpj"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="razaoSocial">Razao Social *</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="razaoSocial"
                  type="text"
                  placeholder="Razao social da empresa"
                  value={razaoSocial}
                  onChange={(e) => setRazaoSocial(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="register-razao-social"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="nomeFantasia">Nome Fantasia *</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="nomeFantasia"
                  type="text"
                  placeholder="Nome fantasia da farmácia"
                  value={nomeFantasia}
                  onChange={(e) => setNomeFantasia(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="register-nome-fantasia"
                />
              </div>
            </div>

            {/* Seção de Endereço com Busca por CEP */}
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h3 className="font-medium text-slate-900 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-teal-600" />
                Endereço da Farmácia
              </h3>
              
              {/* CEP */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="cep">CEP *</Label>
                  <div className="flex gap-2">
                    <Input
                      id="cep"
                      type="text"
                      placeholder="00000-000"
                      value={cep}
                      onChange={(e) => setCep(e.target.value.replace(/\D/g, '').slice(0, 8))}
                      className="flex-1"
                      data-testid="register-cep"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCepSearch}
                      disabled={buscandoCep}
                      className="px-3"
                      title="Buscar CEP"
                    >
                      {buscandoCep ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Search className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="estado">UF</Label>
                  <Input
                    id="estado"
                    type="text"
                    value={estado}
                    onChange={(e) => setEstado(e.target.value.toUpperCase().slice(0, 2))}
                    placeholder="UF"
                    data-testid="register-estado"
                  />
                </div>
              </div>

              {/* Logradouro e Número */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="logradouro">Logradouro *</Label>
                  <Input
                    id="logradouro"
                    type="text"
                    placeholder="Rua, Avenida, etc."
                    value={logradouro}
                    onChange={(e) => setLogradouro(e.target.value)}
                    data-testid="register-logradouro"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="numero">Número</Label>
                  <Input
                    id="numero"
                    type="text"
                    placeholder="Nº"
                    value={numero}
                    onChange={(e) => setNumero(e.target.value)}
                    data-testid="register-numero"
                  />
                </div>
              </div>

              {/* Complemento e Bairro */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="complemento">Complemento</Label>
                  <Input
                    id="complemento"
                    type="text"
                    placeholder="Sala, Loja, etc."
                    value={complemento}
                    onChange={(e) => setComplemento(e.target.value)}
                    data-testid="register-complemento"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bairro">Bairro</Label>
                  <Input
                    id="bairro"
                    type="text"
                    placeholder="Bairro"
                    value={bairro}
                    onChange={(e) => setBairro(e.target.value)}
                    data-testid="register-bairro"
                  />
                </div>
              </div>

              {/* Cidade */}
              <div className="space-y-2">
                <Label htmlFor="cidade">Cidade *</Label>
                <Input
                  id="cidade"
                  type="text"
                  placeholder="Cidade"
                  value={cidade}
                  onChange={(e) => setCidade(e.target.value)}
                  data-testid="register-cidade"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-teal-600 hover:bg-teal-700 mt-6"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Criando conta...
                </>
              ) : (
                "Criar conta"
              )}
            </Button>

            <p className="text-center text-sm text-slate-500 mt-4">
              Já tem uma conta?{" "}
              <Link to="/login" className="text-teal-600 hover:underline font-medium">
                Fazer login
              </Link>
            </p>
            
            <p className="text-center text-xs text-slate-400 mt-4">
              Ao criar sua conta, você concorda com nossos{" "}
              <Link to="/termos" className="text-teal-600 hover:underline">
                Termos de Uso e Política de Privacidade
              </Link>
            </p>
          </form>
        </div>
      </div>

      {/* Image Section */}
      <div className="hidden lg:flex lg:flex-1 bg-gradient-to-br from-teal-600 to-teal-800 items-center justify-center p-12">
        <div className="max-w-md text-center text-white">
          {/* Logomarca */}
          <div className="mb-8 flex justify-center">
            <picture>
              <source srcSet="/images/logomarca.webp" type="image/webp" />
              <img 
                src="/images/logomarca.png" 
                alt="Conforme a Resolução" 
                className="w-64 h-auto drop-shadow-2xl"
                loading="eager"
                decoding="async"
                width="256"
                height="256"
              />
            </picture>
          </div>
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>
            Gestão Documental Completa
          </h2>
          <p className="text-teal-100 text-lg">
            Controle licenças, calibrações, análises e toda a documentação regulatória da sua farmácia de manipulação.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
